import { disconnectPush, notificationAccountChanged } from 'src/utils/notificationPush';
import PropTypes from 'prop-types';
import {
  createContext,
  useEffect,
  useReducer,
  useCallback,
  useRef
} from 'react';
// supabase
import {
  supabase,
  AUTH_LINK_TYPE,
  AUTH_RECOVERY_KEY,
  AUTH_STORAGE_KEY
} from '../utils/supabaseClient';
// where Supabase may send the browser back to
import { authRedirectUrl } from '../utils/authRedirect';
// Microsoft sign-in: the note the button leaves, and the words for its return
import {
  MICROSOFT_PROVIDER,
  SSO_RETURN,
  cleanCallbackUrl,
  clearSsoAttempt,
  describeSsoError,
  safeReturnPath,
  settleSsoOutcome,
  writeSsoAttempt
} from '../utils/ssoAttempt';

// ----------------------------------------------------------------------
// Supabase-backed auth context.
//
// The PUBLIC shape of this context is intentionally unchanged from the old
// JWT implementation — `{ isAuthenticated, isInitialized, user, type }` plus
// `logout()` — so the ~216 components and the route guards that consume
// useAuth() keep working without edits. What changed is the source of truth:
//   * the session lives in Supabase (auto-refreshed, no 60s /verify-token poll)
//   * `user` is the legacy-shaped account loaded from lingoserver's
//     /verify-token, which now returns the CURRENT Mongo account for a verified
//     Supabase token (id, email, role, type, companyIDs, ...).
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// Sessions that exist only to set a password.
//
// A recovery link and an invite link both sign the user in: GoTrue has no
// scoped "may only change the password" token, so `updateUser({ password })`
// needs an ordinary session, refresh token included. Left alone, that session
// is a full login — click the link, type /dashboard instead of filling the
// form, and you are in without ever choosing a password, on a session that
// outlives the link's own expiry.
//
// So the context remembers that the session came from such a link, and the
// guards send a flagged session to the portal's new-password page and nowhere
// else. The flag lives in localStorage, next to the session it qualifies: it
// has to survive the redirect from wherever the link landed to the form, and a
// fresh tab, since the session itself persists across tabs too. It is cleared
// when the password is set, on sign-out, and by any sign-in that is NOT from
// such a link — a password login or a magic link is a legitimate session.
// ----------------------------------------------------------------------
const RECOVERY_KEY = AUTH_RECOVERY_KEY;
const SET_PASSWORD_LINKS = ['recovery', 'invite'];

function readRecoveryFlag() {
  try {
    return window.localStorage.getItem(RECOVERY_KEY) === '1';
  } catch {
    return false;
  }
}

function writeRecoveryFlag(on) {
  try {
    if (on) window.localStorage.setItem(RECOVERY_KEY, '1');
    else window.localStorage.removeItem(RECOVERY_KEY);
  } catch {
    // Storage unavailable (private mode, blocked): the in-memory state still
    // covers this tab, which is where the link was opened.
  }
}

// Decided at load, from the fragment supabase-js is about to consume. This is
// deliberately not left to the PASSWORD_RECOVERY event alone: supabase-js
// emits it on a zero-delay timer once it has saved the session, which can be
// before React has mounted and subscribed, and an invite arrives as a plain
// SIGNED_IN anyway.
if (AUTH_LINK_TYPE) {
  writeRecoveryFlag(SET_PASSWORD_LINKS.includes(AUTH_LINK_TYPE));
}

// ----------------------------------------------------------------------
// Microsoft sign-in (the intranet's "Continue with Microsoft").
//
// Supabase's `azure` provider, on the same implicit flow as every email link,
// so the session it brings back is an ordinary Supabase session: persisted,
// refreshed and signed out exactly like a password one, and resolved to the
// Mongo account the same way — `/verify-token` reads `app_metadata.mongoId`,
// which only the server writes, and NEVER matches on the email. A Microsoft
// login that Supabase links to an existing user (the same, verified address)
// therefore lands on that user's account with that user's role; one it does
// not link arrives as a new auth user with no mapping, is refused here
// (`UNMAPPED`), signed out of this tab, and told so — it can neither reach
// somebody else's record nor create or change one.
//
// `sso.status`: idle · redirecting (leaving for Microsoft) · returning (back
// with a callback, the account not resolved yet) · failed (`sso.error` says
// why, in the words of `ssoAttempt.js`).
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// WHETHER THIS LOAD HAS ANYTHING TO RESOLVE before the auth state is known.
//
// `isInitialized` used to start false on every load while the guest guards
// never waited for it: the sign-in page was what they rendered until the
// account came back. After Microsoft that is two round trips in a row —
// supabase-js checking the tokens in the URL, then /verify-token — about a
// second of the sign-in form the person had just got past (2026-10-11), and a
// shorter flash on every reload of a sign-in route by someone signed in.
//
// The guards now wait (GuestGuard.js). So that a visitor with nothing to
// resolve is not kept waiting for nothing, the answer is known synchronously
// here: no session persisted by supabase-js and no sign-in callback in the
// URL means "signed out", final, from the first render. Anything else — a
// stored session (restored, refreshed or found expired), a Microsoft return,
// an email link's tokens — starts unresolved and is settled by the effect
// below. Storage that cannot be read counts as "maybe": a moment's wait is
// better than a flash.
// ----------------------------------------------------------------------
function hasPersistedSession() {
  try {
    return Boolean(window.localStorage.getItem(AUTH_STORAGE_KEY));
  } catch {
    return true;
  }
}

/** A sign-in callback (Microsoft, an email link) is being taken in by this load. */
export const AUTH_CALLBACK_AT_LOAD = Boolean(SSO_RETURN || AUTH_LINK_TYPE);

const RESOLVING_AT_LOAD = AUTH_CALLBACK_AT_LOAD || hasPersistedSession();

const initialState = {
  isAuthenticated: false,
  isInitialized: !RESOLVING_AT_LOAD,
  user: null,
  type: '',
  passwordRecovery: readRecoveryFlag(),
  sso: { status: SSO_RETURN ? 'returning' : 'idle', error: null }
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user, type } = action.payload;
    return { ...state, isAuthenticated, isInitialized: true, user, type };
  },
  SESSION: (state, action) => {
    const { isAuthenticated, user, type } = action.payload;
    return { ...state, isAuthenticated, user, type };
  },
  RECOVERY: (state, action) => ({
    ...state,
    passwordRecovery: Boolean(action.payload)
  }),
  SSO: (state, action) => ({ ...state, sso: action.payload }),
  // `sso` is left alone on purpose: a refused Microsoft login signs out AND
  // says why, and the sign-out must not wipe the words it is the reason for
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
    type: '',
    passwordRecovery: false
  })
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext({
  ...initialState,
  method: 'supabase',
  signInWithPassword: () => Promise.resolve(),
  signInWithMicrosoft: () => Promise.resolve(),
  clearSsoError: () => {},
  signInWithOtp: () => Promise.resolve(),
  resetPasswordForEmail: () => Promise.resolve(),
  updatePassword: () => Promise.resolve(),
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve()
});

// ----------------------------------------------------------------------

// Load the legacy-shaped account for the current Supabase session. lingoserver
// verifies the token and returns the CURRENT Mongo document, so role/permission
// changes and de-provisioning take effect on the next call.
// Returns { account, reason }. The reason matters: collapsing every failure to
// null is what let sign-in fail silently — Supabase accepted the password, this
// lookup returned null, the guards kept isAuthenticated false, and the login
// form (whose await had already resolved without throwing) showed nothing at
// all. A login screen must never do that.
//
//   ok          the account resolved
//   rejected    lingoserver verified the token and refused it: no Mongo record
//               behind app_metadata.mongoId, or the account is inactive
//   unreachable the request never completed — server down, CORS, offline
// A rejection now also carries what the server said: `code`
// (ACCOUNT_DISABLED / ACCOUNT_DELETED / ACCOUNT_BANNED / NOT_FOUND) and the
// `message` written for the person reading the screen. "This account has been
// deactivated. Contact your manager." is a very different thing to say than
// the generic "not active on this portal", and only the server knows which.
// Keyed by the exact access token, so one sign-in does not hit /verify-token
// twice — once here and again when onAuthStateChange fires for the same
// session. A refreshed token is a different string and therefore re-fetches, so
// this removes the duplicate without making the account any staler: the point
// of the call is that role, status and de-provisioning take effect on the next
// token, not at some cache's convenience.
let lastVerified = { token: null, result: null };

// The request in flight, by token. The memo above only holds an ANSWER, so two
// calls for the same token at the same moment both went to the server — and
// that happens on every load with a stored session (getSession() and the
// INITIAL_SESSION event) and on every password sign-in (signInWithPassword
// and SIGNED_IN). They now share one request.
let inflight = { token: null, promise: null };

// `force` skips the per-token memo — the watchdog below needs a real answer
// about the CURRENT token, not the verdict cached when this tab signed in.
async function fetchAccount(accessToken, force = false) {
  if (!accessToken) return { account: null, reason: 'rejected' };
  if (!force && lastVerified.token === accessToken && lastVerified.result) {
    return lastVerified.result;
  }
  if (!force && inflight.token === accessToken && inflight.promise) {
    return inflight.promise;
  }
  const promise = requestAccount(accessToken);
  if (!force) inflight = { token: accessToken, promise };
  try {
    return await promise;
  } finally {
    if (inflight.promise === promise) inflight = { token: null, promise: null };
  }
}

async function requestAccount(accessToken) {
  try {
    const res = await fetch(
      `${process.env.REACT_APP_APOLLO_SERVER}/verify-token`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    // 5xx is the server failing, not a verdict on this account.
    if (res.status >= 500) return { account: null, reason: 'unreachable' };
    if (!res.ok) return { account: null, reason: 'rejected' };
    const data = await res.json();
    const result =
      data?.valid && data?.user
        ? { account: data.user, reason: 'ok' }
        : {
            account: null,
            reason: 'rejected',
            code: data?.code || null,
            message: data?.message || null
          };
    // Only a verdict is cached; 'unreachable' stays retryable on purpose.
    lastVerified = { token: accessToken, result };
    return result;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('fetchAccount failed:', err?.message);
    return { account: null, reason: 'unreachable' };
  }
}

// Which portal each account type belongs to. `type` comes from lingoserver's
// accountMapping: employees are "user", companyContacts "Client", and vendors
// carry their own role (Linguist / Technician / Formatter / Marketeer).
export const PORTAL_FOR_TYPE = {
  user: { path: '/intranet', label: 'Team' },
  Client: { path: '/clients', label: 'Clients' },
  Linguist: { path: '/vendors', label: 'Linguists' },
  Technician: { path: '/vendors', label: 'Linguists' },
  Formatter: { path: '/vendors', label: 'Linguists' },
  Marketeer: { path: '/vendors', label: 'Linguists' }
};

/** The account types each portal accepts, for signInWithPassword. */
export const PORTAL_TYPES = {
  intranet: ['user'],
  clients: ['Client'],
  vendors: ['Linguist', 'Technician', 'Formatter', 'Marketeer']
};

/**
 * Where a session that may only set a password has to go: the new-password
 * page of the portal that owns the account. Falls back to the client portal,
 * which is where invites (the one flow with no prior account type on the
 * client side) are sent.
 */
export function newPasswordPathFor(type) {
  const portal = PORTAL_FOR_TYPE[type];
  return `${portal ? portal.path : '/clients'}/new-password`;
}

AuthProvider.propTypes = { children: PropTypes.node };

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setRecovery = useCallback((on) => {
    writeRecoveryFlag(on);
    dispatch({ type: 'RECOVERY', payload: on });
  }, []);

  // Reconcile React state with a Supabase session (or its absence).
  const syncSession = useCallback(async (session, initialize = false) => {
    const accessToken = session?.access_token;
    const { account } = accessToken
      ? await fetchAccount(accessToken)
      : { account: null };
    await notificationAccountChanged(account?.id || null);
    const payload = account
      ? { isAuthenticated: true, user: account, type: account.type || '' }
      : { isAuthenticated: false, user: null, type: '' };
    dispatch({ type: initialize ? 'INITIALIZE' : 'SESSION', payload });
  }, []);

  // While the return from Microsoft is being settled, the auth events do not
  // reconcile state on their own: SIGNED_IN for the same session would
  // otherwise race the decision below and could mark a refused account
  // signed in after it had been signed out.
  const settling = useRef(Boolean(SSO_RETURN));

  // THE RETURN FROM MICROSOFT, settled once: the account behind the session
  // is asked for exactly as a password login asks for it, and the portal's
  // own account types decide. Anything else is signed out of THIS tab
  // (`scope: 'local'`, so an account that is valid on another portal keeps
  // its sessions there) and the reason stays on the sign-in form.
  const settleSso = useCallback(
    async (session) => {
      const attempt = SSO_RETURN;
      clearSsoAttempt();
      const anonymous = { isAuthenticated: false, user: null, type: '' };
      // The outcome before INITIALIZE: these dispatches are not batched here,
      // and in the other order the guards would render one pass with the
      // session resolved and the Microsoft state still "returning".
      const finish = (payload, sso) => {
        settling.current = false;
        dispatch({ type: 'SSO', payload: sso });
        dispatch({ type: 'INITIALIZE', payload });
        cleanCallbackUrl();
      };

      if (!session?.access_token) {
        // the callback carried an error, or tokens supabase-js would not take
        finish(anonymous, {
          status: 'failed',
          error: describeSsoError(attempt?.error || {})
        });
        return;
      }

      const result = await fetchAccount(session.access_token);
      const outcome = settleSsoOutcome({
        result,
        expectedTypes: PORTAL_TYPES[attempt?.portal] || PORTAL_TYPES.intranet,
        email: session.user?.email || '',
        homeOf: (type) => PORTAL_FOR_TYPE[type]
      });

      if (outcome.signedIn) {
        // a Microsoft login is a full session: it clears a flag left by an
        // abandoned recovery link, as a password login does
        setRecovery(false);
        finish(
          {
            isAuthenticated: true,
            user: outcome.account,
            type: outcome.account.type || ''
          },
          { status: 'idle', error: null }
        );
        return;
      }

      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch {
        // the local session is dropped either way
      }
      finish(anonymous, { status: 'failed', error: outcome.error });
    },
    [setRecovery]
  );

  useEffect(() => {
    let active = true;

    (async () => {
      // The guards wait on this, so it must END: a session that cannot be
      // read is no session, and the sign-in page beats waiting forever.
      let session = null;
      try {
        const { data } = await supabase.auth.getSession();
        session = data?.session || null;
      } catch {
        session = null;
      }
      if (!active) return;
      if (settling.current) await settleSso(session);
      else await syncSession(session, true);
    })();

    // Fires on SIGNED_IN, SIGNED_OUT, TOKEN_REFRESHED, USER_UPDATED and on the
    // recovery/magic-link redirect that detectSessionInUrl consumes.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      if (event === 'TOKEN_REFRESHED') window.dispatchEvent(new Event('universe:auth-refreshed'));
      if (event === 'SIGNED_OUT') {
        notificationAccountChanged(null).catch(() => {});
        writeRecoveryFlag(false);
        dispatch({ type: 'LOGOUT' });
        return;
      }
      if (settling.current) return;
      // Belt and braces with the load-time check above: if React did mount in
      // time to hear it, honour it.
      if (event === 'PASSWORD_RECOVERY') setRecovery(true);
      syncSession(session, false);
    });

    return () => {
      active = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, [syncSession, setRecovery, settleSso]);

  // ----------------------------------------------------------------------
  // Session watchdog.
  //
  // Deactivating someone has to close the tab they left open, not wait for it.
  // The server refuses their next GraphQL call, but a tab sitting on a
  // dashboard may not make one for hours, and the Supabase access token is
  // good until it expires. So while a session exists we re-verify it against
  // /verify-token every 5 minutes, and again the moment the tab comes back to
  // the foreground — the realistic case being a laptop reopened after the
  // account was disabled.
  //
  // The check bypasses the per-token memo (`force`): the point is a fresh
  // verdict on the CURRENT token, not the one cached at sign-in. Only an
  // explicit rejection signs out; 'unreachable' means we could not ask, and a
  // flaky network must never throw a working session away.
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (!state.isAuthenticated) return undefined;

    let active = true;

    const verifyNow = async () => {
      const { data } = await supabase.auth.getSession();
      const accessToken = data?.session?.access_token;
      // No session left: SIGNED_OUT has already handled (or is handling) it.
      if (!accessToken) return;
      const { reason } = await fetchAccount(accessToken, true);
      if (!active) return;
      if (reason === 'rejected') await supabase.auth.signOut();
    };

    const interval = setInterval(verifyNow, 5 * 60 * 1000);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') verifyNow();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      active = false;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [state.isAuthenticated]);

  // --- Auth actions ---------------------------------------------------

  // `expectedTypes` is the portal's own account types. Credentials that are
  // perfectly valid somewhere else are still wrong HERE, and saying so is the
  // whole point: a team member typing their real password into the linguist
  // portal used to be bounced back to the same form with no explanation,
  // because the guards simply fall through when the type does not match.
  const signInWithPassword = useCallback(
    async (email, password, expectedTypes) => {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: String(email || '')
          .trim()
          .toLowerCase(),
        password
      });
      if (error) throw error;

      // Correct credentials are only half of a sign-in here: the portals also
      // need the Mongo account behind them. Resolve it NOW and fail loudly,
      // rather than let onAuthStateChange discover it and quietly leave
      // isAuthenticated false — which looked to the user like pressing Sign in
      // did nothing at all, with no message anywhere.
      const { account, reason, message } = await fetchAccount(
        data?.session?.access_token
      );
      if (!account) {
        // Don't leave a half-session behind that the guards will reject on
        // every subsequent page while the user believes they are signed in.
        await supabase.auth.signOut();
        if (reason === 'unreachable') {
          throw new Error(
            'We could not reach the LingoYou server. Your password was accepted — please try again in a moment.'
          );
        }
        // The server knows WHY it refused (deactivated, deleted, banned) and
        // says so in plain words; the generic line is only the fallback.
        throw new Error(
          message ||
            'Your password is correct, but this account is not active on this portal. If you use a different LingoYou portal, sign in there; otherwise contact support@lingoyou.com.'
        );
      }

      // Right credentials, wrong door.
      if (expectedTypes && !expectedTypes.includes(account.type)) {
        await supabase.auth.signOut();
        const home = PORTAL_FOR_TYPE[account.type];
        const err = new Error(
          home
            ? `Those are the right details, but this is a ${home.label} account. Sign in on the ${home.label} portal instead.`
            : 'Those are the right details, but this account cannot be used on this portal.'
        );
        // The form renders a link to it, so the user does not have to go
        // hunting.
        err.portalPath = home?.path;
        err.portalLabel = home?.label;
        throw err;
      }

      // A password login is a full session in its own right — it clears a flag
      // left behind by an abandoned recovery link.
      setRecovery(false);
      return data;
    },
    [setRecovery]
  );

  // "Continue with Microsoft". Leaves a note for the return (ssoAttempt.js),
  // then hands the browser to Supabase, which sends it to Microsoft and back
  // to `returnTo` — the page the button was on, so a link to a record still
  // opens that record — on the origin Supabase is allowed to redirect to
  // (`authRedirectUrl`, as every email link). The account types the portal
  // takes are applied on the way back, in `settleSso`.
  //
  // No `prompt`: someone already signed in to Microsoft 365 goes straight
  // through, and Microsoft shows its own account picker when there is more
  // than one account to choose from.
  const signInWithMicrosoft = useCallback(
    async ({ portal = 'intranet', returnTo } = {}) => {
      const back =
        safeReturnPath(returnTo) ||
        (portal === 'intranet' ? PORTAL_FOR_TYPE.user.path : '/');
      writeSsoAttempt({ provider: MICROSOFT_PROVIDER, portal, returnTo: back });
      dispatch({
        type: 'SSO',
        payload: { status: 'redirecting', error: null }
      });
      const { error } = await supabase.auth.signInWithOAuth({
        provider: MICROSOFT_PROVIDER,
        options: { redirectTo: authRedirectUrl(back), scopes: 'email' }
      });
      if (error) {
        clearSsoAttempt();
        dispatch({
          type: 'SSO',
          payload: {
            status: 'failed',
            error: describeSsoError({ description: error.message })
          }
        });
        throw error;
      }
    },
    []
  );

  // Puts the Microsoft state back to rest: the error has been read (the
  // person is signing in another way now), or the page came back from the
  // browser's back-forward cache still showing "Redirecting to Microsoft…".
  const clearSsoError = useCallback(() => {
    clearSsoAttempt();
    dispatch({ type: 'SSO', payload: { status: 'idle', error: null } });
  }, []);

  // Passwordless login. shouldCreateUser:false so a magic-link request for an
  // unknown address CANNOT create a new account — the portals are invite/
  // provision-only. Supabase silently no-ops for unknown emails (no enumeration).
  const signInWithOtp = useCallback(async (email, emailRedirectTo) => {
    const { data, error } = await supabase.auth.signInWithOtp({
      email: String(email || '')
        .trim()
        .toLowerCase(),
      options: { shouldCreateUser: false, emailRedirectTo }
    });
    if (error) throw error;
    return data;
  }, []);

  const resetPasswordForEmail = useCallback(async (email, redirectTo) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(
      String(email || '')
        .trim()
        .toLowerCase(),
      { redirectTo }
    );
    if (error) throw error;
    return data;
  }, []);

  // Called on the new-password page: detectSessionInUrl has already exchanged
  // the recovery link for a session, so updateUser sets the new password.
  const updatePassword = useCallback(
    async (newPassword) => {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });
      if (error) throw error;
      // The one thing this session was for is done.
      setRecovery(false);
      return data;
    },
    [setRecovery]
  );

  const logout = useCallback(async () => {
    await disconnectPush();
    await supabase.auth.signOut();
    writeRecoveryFlag(false);
    dispatch({ type: 'LOGOUT' });
    // a sign-out the person asked for starts the next sign-in clean
    clearSsoAttempt();
    dispatch({ type: 'SSO', payload: { status: 'idle', error: null } });
  }, []);

  // Back-compat shims for the few flows that historically called login()/
  // register() with a GraphQL payload. Authentication is done by Supabase now;
  // these just reconcile the account view from the current session.
  //
  // login() is called right after a mutation that changed the account — the
  // legacy email check flipping profileStatus, onboarding completing — so the
  // caller needs the record as it is NOW. The per-token cache would hand back
  // the pre-mutation copy for the rest of the token's life, and the guards
  // would keep bouncing the user to the step they just finished.
  const login = useCallback(async () => {
    lastVerified = { token: null, result: null };
    inflight = { token: null, promise: null };
    const { data } = await supabase.auth.getSession();
    await syncSession(data?.session, false);
  }, [syncSession]);

  // Self-registration. With "Confirm email" enabled this returns NO session —
  // Supabase mails a confirmation link and the account cannot sign in until it
  // is clicked. `emailRedirectTo` decides where that link lands, so the caller
  // must pass its own portal: without it the link goes to the project's default
  // Site URL, which drops a linguist on the staff portal.
  const register = useCallback(async (email, password, emailRedirectTo) => {
    const { data, error } = await supabase.auth.signUp({
      email: String(email || '')
        .trim()
        .toLowerCase(),
      password,
      options: emailRedirectTo ? { emailRedirectTo } : undefined
    });
    if (error) throw error;
    return data;
  }, []);

  // "Send it again": Supabase re-sends the signup confirmation to an address
  // that is registered but not yet confirmed. Its default rate limit is one
  // per address per minute; the inbox panel waits the same minute.
  const resendSignUpEmail = useCallback(async (email, emailRedirectTo) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: String(email || '')
        .trim()
        .toLowerCase(),
      options: emailRedirectTo ? { emailRedirectTo } : undefined
    });
    if (error) throw error;
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'supabase',
        signInWithPassword,
        signInWithMicrosoft,
        clearSsoError,
        signInWithOtp,
        resetPasswordForEmail,
        updatePassword,
        login,
        logout,
        register,
        resendSignUpEmail
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
